<template>
	<Card v-if="!hasMicPermission" class="w-full max-w-sm mx-auto mt-10">
		<CardHeader>
			<CardTitle class="text-2xl">
				Permission issue
			</CardTitle>
		</CardHeader>
		<CardContent class="grid gap-4">
			<Alert variant="destructive">
				<AlertCircle class="w-4 h-4" />
				<AlertTitle>Microphone permission error</AlertTitle>
				<AlertDescription>
					Please allow microphone access to move ahead.
				</AlertDescription>
			</Alert>
		</CardContent>
	</Card>
	<Card v-else-if="hasMicPermission && !hasSttPermission" class="w-full max-w-sm mx-auto mt-10">
		<CardHeader>
			<CardTitle class="text-2xl">
				Browser issue
			</CardTitle>
		</CardHeader>
		<CardContent class="grid gap-4">
			<Alert variant="destructive">
				<AlertCircle class="w-4 h-4" />
				<AlertTitle>Use Chrome Browser</AlertTitle>
				<AlertDescription>
					Please use Chrome browser to move ahead.
				</AlertDescription>
			</Alert>
		</CardContent>
	</Card>
	<Card v-else-if="isInvalidInterview" class="w-full max-w-sm mx-auto mt-10">
		<CardHeader>
			<CardTitle class="text-2xl">
				Interview is inactive
			</CardTitle>
		</CardHeader>
		<CardContent class="grid gap-4">
			<Alert variant="destructive">
				<AlertCircle class="w-4 h-4" />
				<AlertTitle>The Interview link is inactive</AlertTitle>
				<AlertDescription>
					Please contact us for further information.
				</AlertDescription>
			</Alert>
		</CardContent>
	</Card>
	<Card v-else-if="isLoading" class="w-full max-w-sm mx-auto mt-10">
		<CardHeader>
			<CardTitle class="text-2xl">
				Loading
			</CardTitle>
		</CardHeader>
		<CardContent class="grid gap-4">
			<Alert variant="default">
				<AlertCircle class="w-4 h-4" />
				<AlertTitle>Loading interview</AlertTitle>
				<AlertDescription>
					Please wait while we load the interview.
				</AlertDescription>
			</Alert>
		</CardContent>
	</Card>
	<Card v-else-if="!hasStartedInterview" class="w-full max-w-sm mx-auto mt-10">
		<CardHeader>
			<CardTitle class="text-2xl">
				Start interview
			</CardTitle>
			<CardDescription>
				Are you ready to start your interview?
			</CardDescription>
		</CardHeader>
		<CardContent class="grid gap-4">
			<Button class="w-full" @click="startInterview()">
				Start Interview
			</Button>
		</CardContent>
	</Card>
	<Card v-else-if="hasEndedInterview" class="w-full max-w-sm mx-auto mt-10">
		<CardHeader>
			<CardTitle class="text-2xl">
				How was your interview
			</CardTitle>
			<CardDescription>
				Share your interview experience with us.
			</CardDescription>
		</CardHeader>
		<CardContent v-if="!reviewSubmitted" class="grid gap-4">
			<div class="flex gap-2">
				<Toggle v-model:pressed="reviewRating.bad" class="flex-1" @click="reviewRating.good = reviewRating.great = false" variant="outline">
					<Bad class="w-4 h-4 mr-2" />
					Bad
				</Toggle>
				<Toggle v-model:pressed="reviewRating.good" class="flex-1" @click="reviewRating.bad = reviewRating.great = false" variant="outline">
					<Good class="w-4 h-4 mr-2" />
					Good
				</Toggle>
				<Toggle v-model:pressed="reviewRating.great" class="flex-1" @click="reviewRating.bad = reviewRating.good = false" variant="outline">
					<Great class="w-4 h-4 mr-2" />
					Great
				</Toggle>
			</div>
			<Textarea v-model="reviewComment" placeholder="Tell us why" class="resize-none"/>
			<Button type="button" class="w-full" @click="submitReview">
				Submit feedback
			</Button>
		</CardContent>
		<CardContent v-else class="grid gap-4">
			<Alert>
				<AlertTitle>Feedback received</AlertTitle>
				<AlertDescription>
					We have receive your feedback, we will get back to you soon. Thank you.
				</AlertDescription>
			</Alert>
		</CardContent>
	</Card>
	<div v-else class="grid h-screen w-full pl-[56px]">
		<aside class="inset-y fixed  left-0 z-20 flex h-full flex-col border-r">
			<div class="border-b p-2">
				<Button variant="outline" size="icon" aria-label="Home">
					<Triangle class="size-5 fill-foreground" />
				</Button>
			</div>
			<nav class="grid gap-1 p-2">
				<!--<TooltipProvider>
					<Tooltip>
						<TooltipTrigger as-child>
							<Button
								variant="ghost"
								size="icon"
								class="rounded-lg"
								aria-label="Playground"
							>
								<SquareTerminal class="size-5" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right" :side-offset="5">
							Playground
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>-->
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger as-child>
							<Button
								variant="ghost"
								size="icon"
								class="rounded-lg bg-muted"
								aria-label="AI"
							>
								<Bot class="size-5" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right" :side-offset="5">
							Interview
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</nav>
			<nav class="mt-auto grid gap-1 p-2">
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger as-child>
							<Button
								variant="ghost"
								size="icon"
								class="mt-auto rounded-lg"
								@click="mode = mode === 'dark' ? 'light' : 'dark'"
							>
								<MoonStar class="h-5 w-5" v-if="mode === 'light'" />
								<Sun class="h-5 w-5" v-else />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right" :side-offset="5">
							Color mode
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger as-child>
							<Button
								variant="ghost"
								size="icon"
								class="mt-auto rounded-lg"
								aria-label="Account"
							>
								<SquareUser class="size-5" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right" :side-offset="5">
							Account
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</nav>
		</aside>
		<div class="flex flex-col">
			<header class="items-center gap-1 bg-background px-4">
				<h1 class="my-2 text-xl font-semibold">
					Interview
				</h1>
			</header>
			<main class="grid flex-1 gap-4 overflow-auto p-4 md:grid-cols-2 lg:grid-cols-2">
				<div class="relative hidden flex-col items-start gap-8 md:flex">
					<Card class="w-full">
						<CardHeader>
							<CardTitle>Question</CardTitle>
						</CardHeader>
						<CardContent>
							<p class="text-xl">{{ activeQuestion }}</p>
						</CardContent>
					</Card>
				</div>
				<div class="relative flex flex-col h-[calc(100vh-75px)] min-h-[50vh] rounded-xl bg-muted/50 p-4">
					<ScrollArea class="flex-1" v-show="false">
						<div class="space-y-4">
							<p class="flex w-max max-w-[75%] flex-col gap-2 rounded-lg px-3 py-2 text-sm bg-muted" v-for="index in 200" :key="index">Test</p>
							<p class="flex w-max max-w-[75%] flex-col gap-2 rounded-lg px-3 py-2 text-sm ml-auto bg-primary text-primary-foreground" v-for="index in 200" :key="index">Test</p>
						</div>
					</ScrollArea>
					<div v-show="true" class="flex flex-1 flex-col gap-4">
						<h3 class="font-semibold leading-none tracking-tight">
							<span v-if="typingEnabled">Speak now</span>
							<span v-else>Question</span>
						</h3>
						<div class="flex flex-col flex-1 rounded-lg border bg-background focus-within:ring-1 focus-within:ring-ring">
							<Textarea
								v-model="msgText"
								placeholder="Type your message here..."
								:disabled="!typingEnabled"
								class="grow resize-none border-0 p-3 shadow-none focus-visible:ring-0 grow"
							/>
							<div class="flex items-center p-3 pt-0">
								<Button @click="submitAnswer" :disabled="!typingEnabled" type="submit" size="sm" class="ml-auto gap-1.5">
									Submit Answer
									<CornerDownLeft class="size-3.5" />
								</Button>
							</div>
						</div>
					</div>
				</div>
			</main>
		</div>
	</div>
</template>
<script setup lang="ts">
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Stepper,
	StepperIndicator,
	StepperItem,
	StepperSeparator,
	StepperTitle,
	StepperTrigger,
} from "@/components/ui/stepper";
import { Toggle } from "@/components/ui/toggle";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
	Frown as Bad,
	Smile as Good,
	Laugh as Great,
	AlertCircle,
	Bot,
	CornerDownLeft,
	MoonStar,
	SquareUser,
	Sun,
	Triangle,
} from "lucide-vue-next";
import { useColorMode } from "@vueuse/core";
import ky from "ky";
import {useRoute, type RouteLocationNormalized} from "vue-router";
import {ref, type Ref} from "vue";

const mode = useColorMode();

let hasMicPermission: Ref<boolean> = ref(true);
let hasSttPermission: Ref<boolean> = ref(false);
let isInvalidInterview: Ref<boolean> = ref(false);
let hasStartedInterview: Ref<boolean> = ref(false);
let hasEndedInterview: Ref<boolean> = ref(false);
let isLoading: Ref<boolean> = ref(true);
let interviewData: Ref<any> = ref({});
let typingEnabled: Ref<boolean> = ref(false);
let msgText: Ref<string> = ref("");
let activeQuestion: Ref<string> = ref("");
let questions: {id: string, challenge_id: string, question: string}[] = [];
let questionCounter = 1;


const audioObjs: { [key: string]: HTMLAudioElement } = {
	intro: new Audio("/audio/intro.mp3"),
	question1: new Audio("/audio/question1.mp3"),
	question2: new Audio("/audio/question2.mp3"),
	question3: new Audio("/audio/question3.mp3"),
	question4: new Audio("/audio/question4.mp3"),
	question5: new Audio("/audio/question5.mp3"),
	outro: new Audio("/audio/outro.mp3")
};

const reviewRating: Ref<{ bad: boolean, good: boolean, great: boolean }> = ref({
	bad: false,
	good: false,
	great: false,
});
const reviewComment: Ref<string> = ref("");
const reviewSubmitted: Ref<boolean> = ref(false);

let stt: any = undefined;
let recognition: any = undefined;
const route: RouteLocationNormalized = useRoute();


async function init(): Promise<void> {
	stt = window.SpeechRecognition || window.webkitSpeechRecognition;
	if (typeof stt === "undefined") {
		console.log("STT is disabled", window.SpeechRecognition, window.webkitSpeechRecognition);
		return;
	}
	hasSttPermission.value = true;
	recognition = setupSTT(stt);
	recognition.start();
	setTimeout(() => {
		main();
	}, 1000);
}

init();

async function main(): Promise<void> {
	// Load all audio files
	const audioPromise: Promise<void>[] = [];
	for (const objName in audioObjs) {
		audioPromise.push(loadAudioAsync(audioObjs[objName]));
	}
	await Promise.all(audioPromise);
	await getInterview();
	recognition.abort();
	msgText.value = "";
	isLoading.value = false;
}

function setupSTT(stt: any): any {
	const recognition = new stt();
	/* Set the speech recognition to continuous so it keeps listening to whatever you say. This way you can record long texts, conversations and so on. */
	recognition.continuous = true;
	recognition.interimResults = true;
	/* Sets the language for speech recognition. It uses IETF tags, ISO 639-1 like en-GB, en-US, es-ES and so on */
	recognition.lang = "en-GB";
	// Error handling
	recognition.addEventListener("error", (event: {error: string, message: string}) => {
		if (["not-allowed", "service-not-allowed"].includes(event.error)) {
			console.log("Speech recognition permission denied", event.error);
			hasMicPermission.value = false;
			return;
		}
		console.error("Speech recognition error detected", event.error, event.message);
	});
	// Event triggered when it gets a match
	recognition.onresult = (e: any) => {
		msgText.value = "";
		// iterate through speech recognition results
		for (const result of e.results) {
			msgText.value += "\n" + result[0].transcript.trim();
			console.log("STT results", e.results);
		}
	}
	// Additional logging
	recognition.addEventListener("start", () => {
		console.log("Speech recognition service has started");
	});
	recognition.addEventListener("audiostart", () => {
		console.log("Audio capturing started");
	});
	recognition.addEventListener("speechstart", () => {
		console.log("Speech has been detected");
	});
	recognition.addEventListener("soundstart", () => {
		console.log("Some sound is being received");
	});
	recognition.addEventListener("end", () => {
		console.log("Speech recognition service disconnected");
	});
	recognition.addEventListener("audioend", () => {
		console.log("Audio capturing ended");
	});
	recognition.addEventListener("speechend", () => {
		console.log("Speech has stopped being detected");
	});
	recognition.addEventListener("soundend", () => {
		console.log("Sound has stopped being received");
	});
	recognition.addEventListener("nomatch", (event: any) => {
		console.error("Speech not recognized", event);
	});
	return recognition
}

async function getInterview() {
	try {
		interviewData.value = await ky.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/interview/interview/${route.params.interview_id}/`
		).json();
	} catch (e) {
		isInvalidInterview.value = true;
	}
}

async function startInterview() {
	try {
		let resp: {
			id: string
			name: string
			status: number
			started_at: string
			max_duration: number
			challenges: {
				id: string
				name: string
				description: string
				questions: {id: string, question: string}[]
			}[]
		} = await ky.put(
			`${import.meta.env.VITE_API_HOST}/api/v1/interview/interview/${route.params.interview_id}/`,
			{
				json: {
					action: "start"
				}
			}
		).json();
		hasStartedInterview.value = true;
		for (let challenge of resp.challenges) {
			for (let question of challenge.questions) {
				questions.push({
					id: question.id,
					challenge_id: challenge.id,
					question: question.question
				});
			}
		}

		await playAudioAsync(audioObjs["intro"]);
		await triggerQuestion();
	} catch (e) {
		console.log(e);
		isInvalidInterview.value = true;
	}
}

async function triggerQuestion(): Promise<void> {
	if (questionCounter > questions.length) {
		console.log("playing outro");
		await playAudioAsync(audioObjs["outro"]);
		hasEndedInterview.value = true;
		return;
	}
	activeQuestion.value = questions[questionCounter - 1].question;
	try {
		console.log("before play audio", audioObjs[`question${questionCounter}`]);
		await playAudioAsync(audioObjs[`question${questionCounter}`]);
		console.log("after play audio");
		recognition.start();
		console.log("after recognition");
	}
	catch (e) {
		console.error("trigger question err", questionCounter, e);
	}
	typingEnabled.value = true;
}

async function submitAnswer() {
	await ky.post(
		`${import.meta.env.VITE_API_HOST}/api/v1/interview/interview/${route.params.interview_id}/`,
		{
			json: {
				challenge_id: questions[questionCounter - 1].challenge_id,
				question_id: questions[questionCounter - 1].id,
				answer: msgText.value,
			}
		}
	).json();
	msgText.value = "";
	typingEnabled.value = false;
	recognition.stop();
	questionCounter++;
	await triggerQuestion();
}

async function submitReview() {
	await ky.put(
		`${import.meta.env.VITE_API_HOST}/api/v1/interview/interview/${route.params.interview_id}/`,
		{
			json: {
				action: "review",
				rating: reviewRating.value.bad ? "bad" : reviewRating.value.good ? "good" : "great",
				comment: reviewComment.value,
			}
		}
	).json();
	reviewSubmitted.value = true;
}

function loadAudioAsync(audio: HTMLAudioElement): Promise<void> {
	return new Promise((resolve, reject) => {
		audio.addEventListener("loadeddata", () => {
			resolve();
		});
		audio.addEventListener("error", (e) => {
			console.error("audio error in loadAudioAsync", e);
			reject(e);
		});
		audio.load();
	});
}

function playAudioAsync(audio: HTMLAudioElement) {
	return new Promise((resolve, reject) => {
		audio.addEventListener("ended", resolve, { once: true });
		audio.addEventListener("error", function (e) {
			console.error("audio error in playAudioAsync", e);
			reject(e);
		}, { once: true });
		audio.play().catch(function (e) {
			console.error("audio.play error", e);
			reject(e);
		});
	});
}
</script>
